/**
 * Derive profile dashboard presentation from match history cards (no duplicate stats storage).
 */
import { APP_NAME } from "../../config/brand";

/**
 * @param {number} completedAtMs
 */
export function formatMatchWhenShort(completedAtMs) {
  if (!completedAtMs) return "";
  const now = new Date();
  const d = new Date(completedAtMs);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * @param {number | null | undefined} ms
 */
export function formatMemberSince(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/**
 * @param {string} playerId
 */
export function formatPlayerTag(playerId) {
  if (!playerId) return "";
  const tail = playerId.replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase();
  return tail ? `#SS-${tail}` : "";
}

/**
 * @param {Array<{ matchType?: string }>} cards
 */
export function inferDisciplineLabel(cards, playingCategory) {
  if (playingCategory) {
    const c = String(playingCategory).toLowerCase();
    if (c.includes("double")) return "Doubles player";
    if (c === "singles") return "Singles player";
    return `${playingCategory} player`;
  }
  const counts = { singles: 0, doubles: 0, mixed: 0 };
  for (const card of cards || []) {
    const t = (card.matchType || "").toLowerCase();
    if (t === "singles") counts.singles += 1;
    else if (t === "mixed") counts.mixed += 1;
    else if (t) counts.doubles += 1;
  }
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  if (!top || top[1] === 0) return "Badminton player";
  if (top[0] === "singles") return "Singles player";
  if (top[0] === "mixed") return "Mixed doubles player";
  return "Doubles player";
}

/**
 * @param {Array<{ isWin?: boolean; completedAt?: number }>} cards newest-first
 */
export function computeWinStreak(cards) {
  let streak = 0;
  for (const card of cards || []) {
    if (!card.isWin) break;
    streak += 1;
  }
  return streak;
}

/**
 * Consecutive calendar days (ending today) with at least one completed match.
 * @param {Array<{ completedAt?: number }>} cards newest-first
 */
export function computeActiveDayStreak(cards) {
  const dayKeys = new Set();
  for (const card of cards || []) {
    if (!card.completedAt) continue;
    const d = new Date(card.completedAt);
    dayKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  if (!dayKeys.size) return 0;
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  for (;;) {
    const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
    if (!dayKeys.has(key)) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * @param {number} [year]
 */
export function currentSeasonLabel(year = new Date().getFullYear()) {
  return String(year);
}

/**
 * @param {Array<{ isWin?: boolean }>} cards
 */
export function computeCareerWinRate(cards) {
  const list = cards || [];
  if (!list.length) return null;
  const wins = list.filter((c) => c.isWin).length;
  return Math.round((wins / list.length) * 100);
}

/**
 * @param {Array<{ isWin?: boolean; completedAt?: number }>} cards newest-first
 */
export function computeSeasonStats(cards) {
  const year = new Date().getFullYear();
  const season = (cards || []).filter((c) => {
    if (!c.completedAt) return false;
    return new Date(c.completedAt).getFullYear() === year;
  });
  const matches = season.length;
  const wins = season.filter((c) => c.isWin).length;
  const losses = matches - wins;
  const winRate = matches ? Math.round((wins / matches) * 100) : null;
  return { matches, wins, losses, winRate };
}

/**
 * @param {Array<{ isWin?: boolean }>} cards newest-first
 * @param {number} [count]
 */
export function recentFormLetters(cards, count = 5) {
  return (cards || []).slice(0, count).map((c) => (c.isWin ? "W" : "L"));
}

/**
 * @typedef {{ kind: string; text: string; when: string; tone: 'win' | 'purple' | 'amber' | 'muted' }} JourneyRow
 */

/**
 * @param {Array<{ isWin?: boolean; opponentsLabel?: string; resultHeadline?: string; completedAt?: number; competitionId?: string | null }>} cards newest-first
 * @param {number | null} memberSinceMs
 * @returns {JourneyRow[]}
 */
export function buildJourneyRows(cards, memberSinceMs) {
  /** @type {JourneyRow[]} */
  const rows = [];
  const chronological = [...(cards || [])].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  const competitionMatch = chronological.find((c) => c.competitionId);
  if (competitionMatch) {
    rows.push({
      kind: "tournament",
      text: "Tournament debut",
      when: formatMatchWhenShort(competitionMatch.completedAt),
      tone: "purple",
    });
  }

  const winsChronological = chronological.filter((c) => c.isWin);
  if (winsChronological.length >= 10) {
    const tenth = winsChronological[9];
    rows.push({
      kind: "milestone",
      text: "10th career win",
      when: formatMatchWhenShort(tenth.completedAt),
      tone: "amber",
    });
  }

  const since = formatMemberSince(memberSinceMs);
  if (since) {
    rows.push({
      kind: "joined",
      text: `Joined ${APP_NAME}`,
      when: since,
      tone: "muted",
    });
  }

  return rows.slice(0, 4);
}
